// Shared workout helper functions and constants

// Helper function to get current ISO week number
export function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
}

// Helper to determine if module type uses weight-based tracking
export function isWeightBasedModule(moduleType) {
  const weightTypes = ['strength', 'endurance_strength', 'mix', null, undefined, '']
  return weightTypes.includes(moduleType?.toLowerCase())
}

// Helper to determine if an EXERCISE (not a module) is tracked with
// sets/reps/weight. Cardio-like exercises don't use sets at all (km +
// duration), so showing the DB-default `training_sets=5` for them is
// misleading. Matches backend ExerciseTypeEnum in workout_enhanced.py.
export function isSetBasedExerciseType(exerciseType) {
  const t = exerciseType?.toLowerCase()
  // Strength uses sets x reps x weight. Tabata uses rounds (≈ sets).
  // Everything else (cardio, running, cycling, swimming, group_class,
  // flexibility) is distance/duration-based.
  return ['strength', 'tabata', 'other', null, undefined, ''].includes(t)
}

// VH-144: turn an exercise definition's configured `custom_metrics` into an
// ordered field list. Mirrors `_fields_from_custom_metrics` in the backend's
// session_capture_service so the template screen and the capture screen agree
// on what an exercise measures.
//
// Field order is preserved on SQLite but NOT on Postgres, where JSONB sorts
// keys by length (noted in VH-139). Callers must not rely on the order being
// the one the author typed.
export function fieldsFromCustomMetrics(customMetrics) {
  if (!customMetrics || typeof customMetrics !== 'object') return []
  return Object.entries(customMetrics)
    .filter(([, def]) => def && typeof def === 'object')
    .map(([key, def]) => ({
      key,
      label_en: def.label_en || key,
      label_de: def.label_de || def.label_en || key,
      unit: def.unit || null
    }))
}

// VH-144: localized label for a metric field. Same rule as `getFieldLabel` in
// useFieldTemplates, but without the hook — the template table renders one of
// these per row and has no need for the field-templates API query.
//
// Only `label_en` / `label_de` exist on a configured metric; pt-BR therefore
// falls back to English rather than showing a raw key.
export function metricFieldLabel(field, language = 'en') {
  if (!field) return ''
  return language === 'de' ? (field.label_de || field.label_en) : field.label_en
}

// VH-144: what a template row should advertise as its target.
//
// The precedence is the one VH-139 established for capture: an exercise's own
// configured fields win over the generic per-type template, and HAVING them
// implies the exercise is not set-based — they describe the whole effort (a
// hike's duration), not one set of it. Branching on `exercise_type` alone was
// the bug: a cardio exercise could only ever render a dash, so the three
// metrics configured on "Walking / Hiking" were invisible in the template.
//
// Returns either {kind:'sets', warmupSets, trainingSets} or
// {kind:'metrics', fields}. `kind:'sets'` with zeroed counts is the fallback
// for an exercise that is neither set-based nor configured — better than a
// dash, because it still says which shape the row is.
export function resolveTemplateTargets(exerciseDef, exercise = {}) {
  const fields = fieldsFromCustomMetrics(exerciseDef?.custom_metrics)
  if (fields.length > 0) return { kind: 'metrics', fields }

  if (isSetBasedExerciseType(exerciseDef?.exercise_type)) {
    return {
      kind: 'sets',
      warmupSets: exercise.warmup_sets || 0,
      trainingSets: exercise.training_sets || 0
    }
  }

  // Non-set-based and unconfigured: nothing truthful to show.
  return { kind: 'none', fields: [] }
}

// Helper to get the module type category for display purposes
export function getModuleCategory(moduleType) {
  const type = moduleType?.toLowerCase()
  if (['cardio', 'running', 'cycling', 'swimming'].includes(type)) return 'cardio'
  if (['hiit', 'tabata'].includes(type)) return 'hiit'
  if (type === 'recovery') return 'recovery'
  if (type === 'group_class') return 'group_class'
  return 'strength'
}

// Exercise type icons and colors
export const EXERCISE_TYPE_CONFIG = {
  strength: { icon: '💪', color: '#EF4444' },
  cardio: { icon: '🏃', color: '#3B82F6' },
  running: { icon: '👟', color: '#10B981' },
  cycling: { icon: '🚴', color: '#F59E0B' },
  swimming: { icon: '🏊', color: '#06B6D4' },
  tabata: { icon: '⏱️', color: '#8B5CF6' },
  hiit: { icon: '🔥', color: '#EC4899' },
  group_class: { icon: '👥', color: '#F97316' },
  flexibility: { icon: '🧘', color: '#14B8A6' },
  other: { icon: '⚡', color: '#6B7280' }
}
